import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { DashboardOrderSummary, OrderStatus } from '../../types';
import { getDashboardOrderColor, normalizeOrderStatus } from '../../utils';
import { getOrderStatusIcon } from './order-status-icons';

interface OrdersMorePopupProps {
  open: boolean;
  date: string; // ISO date string
  orders: DashboardOrderSummary[];
  showPONumbers: boolean;
  onClose: () => void;
  onOrderClick: (order: DashboardOrderSummary) => void;
}

export const OrdersMorePopup = ({
  open,
  date,
  orders,
  showPONumbers,
  onClose,
  onOrderClick,
}: OrdersMorePopupProps) => {
  const dateObj = DateTime.fromISO(date);
  const formattedDate = dateObj.isValid ? dateObj.toFormat('MMMM d, yyyy') : date;

  const handleOrderClick = (order: DashboardOrderSummary) => {
    onOrderClick(order);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          pr: 4,
        }}
      >
        <Typography variant="h6" component="div">
          Orders for {formattedDate}
        </Typography>
        <Tooltip title="Close">
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 8, right: 8 }}
            size="small"
            aria-label="Close dialog"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        {orders.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No orders found for this date.
          </Typography>
        ) : (
          <List>
            {orders.map(order => {
              const displayText = showPONumbers ? order.po : order.orderNumber;
              const normalizedStatus = normalizeOrderStatus(order.status);
              const statusIcon = getOrderStatusIcon(normalizedStatus);
              // Ready for Pickup orders are always green, otherwise use due date based colors
              const backgroundColor =
                normalizedStatus === OrderStatus.READY_FOR_PICKUP
                  ? '#4CAF50' // Green for Ready for Pickup
                  : getDashboardOrderColor(order.colorCode);
              return (
                <ListItem key={order.id} disablePadding>
                  <ListItemButton
                    onClick={() => handleOrderClick(order)}
                    aria-label={`View order ${displayText}`}
                    sx={{
                      backgroundColor: backgroundColor,
                      color: '#FFFFFF',
                      '&:hover': {
                        backgroundColor: backgroundColor,
                        opacity: 0.9,
                      },
                      mb: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography variant="body1" component="span">
                        {statusIcon}
                      </Typography>
                      <Typography
                        variant="body1"
                        component="span"
                        sx={{ flex: 1, fontWeight: 500 }}
                      >
                        {displayText}
                      </Typography>
                      <Typography variant="body2" component="span" sx={{ opacity: 0.9 }}>
                        {order.dealerName}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};
